import { t } from 'i18next'

import { cn } from '../../utils/common'
import { ArrowLeft, ArrowRight } from '../Icons'

export type TablePaginationProps = {
  readonly page: number
  readonly totalPages: number
  readonly setPage: (page: number) => void
}

function buildPageItems(
  currentZeroBased: number,
  total: number
): (number | 'dots')[] {
  if (total <= 9) return Array.from({ length: total }, (_, i) => i + 1)

  const cur = currentZeroBased + 1
  const first3 = [1, 2, 3]
  const last3 = [total - 2, total - 1, total]
  if (cur <= 3) return [...first3, 'dots', ...last3]
  if (cur >= total - 2) return [...first3, 'dots', ...last3]

  return [1, 'dots', cur - 1, cur, cur + 1, 'dots', total]
}

export default function TablePagination({
  page,
  totalPages,
  setPage,
}: TablePaginationProps) {
  const total = Math.max(totalPages, 1)
  const isFirst = page <= 0
  const isLast = page >= total - 1
  const items = buildPageItems(page, total)

  return (
    <nav aria-label="Pagination" className="pt-3 pb-4 pr-6">
      <ul
        className={cn(
          'inline-flex items-stretch list-none border rounded-lg divide-gray-200 border-gray-200 p-0 m-0'
        )}
      >
        <li>
          <button
            type="button"
            onClick={() => !isFirst && setPage(page - 1)}
            disabled={isFirst}
            className={cn(
              'inline-flex items-center gap-[6px] h-10 px-3 bg-white border border-solid rounded-l-lg shadow-none appearance-none border-gray-200 font-semibold text-gray-500 hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed focus:outline-none cursor-pointer'
            )}
          >
            <ArrowLeft className="text-gray-400" aria-hidden />
            <span>{t('components.table.previous')}</span>
          </button>
        </li>

        {items.map((it, i) =>
          it === 'dots' ? (
            <li
              key={`dots-${it}-${i}`}
              className={cn(
                'inline-flex items-center justify-center h-10 px-3 border border-solid text-gray-400 border-gray-200'
              )}
            >
              …
            </li>
          ) : (
            <li key={it}>
              <button
                type="button"
                onClick={() => setPage(it - 1)}
                aria-current={page === it - 1 ? 'page' : undefined}
                className={cn(
                  'inline-flex h-10 w-10 items-center justify-center tabular-nums border gap-2 border-gray-200 focus:outline-none focus:ring-0 bg-white border-solid shadow-none cursor-pointer',
                  page === it - 1
                    ? 'text-gray-900 font-medium border bg-gray-dark-25 border-[--Colors-Gray-light-300]'
                    : 'text-gray-400 hover:bg-gray-50 border-gray-200'
                )}
              >
                {it}
              </button>
            </li>
          )
        )}

        <li>
          <button
            type="button"
            onClick={() => !isLast && setPage(page + 1)}
            disabled={isLast}
            className={cn(
              'inline-flex items-center h-10 gap-[6px] px-3 font-semibold bg-white border border-solid rounded-r-lg shadow-none appearance-none border-gray-200 text-gray-500 hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed focus:outline-none focus:ring-0 cursor-pointer'
            )}
          >
            <span>{t('components.table.next')}</span>
            <ArrowRight className="text-gray-500" aria-hidden />
          </button>
        </li>
      </ul>
    </nav>
  )
}
